/**
 * Page chrome: the animated wrapper and the standard page header.
 *
 * Every page uses these, which is what makes navigation feel like one app
 * rather than fifteen independently animated screens. All timings come from
 * `tokens.motion` — the lint rule rejects a hand-written `{ duration: 0.2 }`.
 */
import { type ReactNode } from 'react';
import Box, { type BoxProps } from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { motion as Motion } from 'motion/react';

import { tokens } from '@/theme/tokens';

export interface PageContainerProps extends Omit<BoxProps, 'title'> {
  children: ReactNode;
  /** 'narrow' for single-column reading pages, 'wide' for grids and tables. */
  width?: 'narrow' | 'wide' | 'full';
}

export function PageContainer({ children, width = 'wide', sx, ...rest }: PageContainerProps) {
  const maxWidth =
    width === 'narrow'
      ? tokens.size.narrowMaxWidth
      : width === 'wide'
        ? tokens.size.contentMaxWidth
        : '100%';

  // Motion wraps Box rather than being composed into it. `Motion.create(Box)`
  // type-conflicts on `onAnimationStart`, which means two different things to
  // React DOM and to Motion, and the nesting is clearer anyway: one element
  // animates, the other lays out.
  return (
    <Motion.div
      initial={tokens.motion.variants.page.initial}
      animate={tokens.motion.variants.page.animate}
      exit={tokens.motion.variants.page.exit}
      transition={tokens.motion.transition.enter}
      style={{ width: '100%' }}
    >
      <Box
        {...rest}
        sx={[
          {
            maxWidth,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            width: '100%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    </Motion.div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Buttons or controls, right-aligned on desktop. */
  actions?: ReactNode;
  /** Rendered above the title — breadcrumbs, back links. */
  overline?: ReactNode;
}

export function PageHeader({ title, subtitle, actions, overline }: PageHeaderProps) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      spacing={2}
      sx={{ mb: 3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        {overline ? (
          <Typography variant="overline" color="text.secondary" component="div">
            {overline}
          </Typography>
        ) : null}
        <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}

/**
 * Staggered list entrance.
 *
 * The stagger is capped at `tokens.motion.staggerMaxItems` so the
 * 204-assignment course does not take forty seconds to finish animating in —
 * an easy thing to get wrong that only shows up on the one course big enough
 * to matter.
 */
export function StaggerItem({ index, children }: { index: number; children: ReactNode }) {
  const delay = Math.min(index, tokens.motion.staggerMaxItems) * tokens.motion.stagger;
  return (
    <Motion.div
      initial={tokens.motion.variants.listItem.initial}
      animate={tokens.motion.variants.listItem.animate}
      transition={{ ...tokens.motion.transition.enter, delay }}
    >
      {children}
    </Motion.div>
  );
}
